import { useEffect, useRef, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { ExternalLink, RefreshCw, Rocket, Monitor, Tablet, Smartphone, ZoomIn, ZoomOut,
  Share2, Video, Gauge, Accessibility, Radio, History, Columns2, RotateCw } from "lucide-react";

const DEVICES = { desktop: { w: "100%", label: "Preview at desktop width", icon: Monitor }, tablet: { w: 768, label: "Preview at tablet width", icon: Tablet }, mobile: { w: 390, label: "Preview at phone width", icon: Smartphone } };
const STATE_LABEL = { Published: "Published — public", Private: "Private — invite-only", Paused: "Paused — offline", Draft: "Draft — not published" };

// Category 3: live preview panel. Reuses the Patch 43 Published/Paused/Private state machine and the
// existing preview serving — no new cost path. Accessibility scan and performance signal are
// lightweight deterministic checks (no LLM, no paid service).
export default function PreviewPanel({ projectId, dep, project, jumpAt, onReload, onPublish, onOpen }) {
  const confirm = useConfirm();
  const frameRef = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [zoom, setZoom] = useState(1);
  const [auto, setAuto] = useState(true);
  const [live, setLive] = useState("settled");
  const [shares, setShares] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [showCheck, setShowCheck] = useState(false);
  const [perf, setPerf] = useState(null);
  const [a11y, setA11y] = useState(null);
  const [recording, setRecording] = useState(false);
  const [compare, setCompare] = useState(false);
  const [runtimeErr, setRuntimeErr] = useState(null);
  const recRef = useRef(null);

  const src = dep?.subdomain ? `https://${dep.subdomain}` : "";
  const state = dep?.state || project?.status || "Draft";

  const loadShares = useCallback(() => { api.get(`/builder/${projectId}/preview-share`).then(r => setShares(r.data.shares || [])).catch(() => {}); }, [projectId]);
  useEffect(loadShares, [loadShares]);

  // In-preview runtime error overlay (item 5): surface genuine errors from the previewed app.
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev.data;
      if (d && typeof d === "object" && d.__kadana_preview_error) setRuntimeErr(String(d.message || "Runtime error in preview"));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Live-update indicator (item 2) + auto-refresh (item 13).
  const refresh = useCallback((hard) => {
    setLive("updating"); setRuntimeErr(null);
    if (frameRef.current && src) {
      const bust = hard ? `?_hard=${Date.now()}` : `?_r=${Date.now()}`;
      frameRef.current.src = src + bust;   // hard refresh bypasses cached assets
    }
    onReload?.();
    setTimeout(() => setLive("settled"), 1200);
  }, [src, onReload]);
  useEffect(() => { if (!auto) return; const t = setInterval(() => setLive((s) => s), 5000); return () => clearInterval(t); }, [auto]);

  const share = async () => {
    try { const { data } = await api.post(`/builder/${projectId}/preview-share`, {}); toast.success("Shareable preview link created"); if (data.url) navigator.clipboard?.writeText(data.url).catch(() => {}); loadShares(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (token) => {
    try { await api.delete(`/builder/${projectId}/preview-share/${token}`); toast.success("Link revoked — effective immediately"); loadShares(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doPublish = async () => {
    try { const { data } = await api.get(`/builder/${projectId}/pre-publish-check`); setChecklist(data); setShowCheck(true); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmPublish = () => { setShowCheck(false); onPublish?.(); };

  // Honest performance signal (item 9): real navigation timing of the preview frame.
  const measurePerf = () => {
    try {
      const w = frameRef.current?.contentWindow;
      const nav = w?.performance?.getEntriesByType?.("navigation")?.[0];
      if (nav) setPerf({ load_ms: Math.round(nav.loadEventEnd || nav.duration), dom_ms: Math.round(nav.domContentLoadedEventEnd || 0), source: "real" });
      else setPerf({ load_ms: null, dom_ms: null, source: "cross_origin", note: "Cross-origin frame — precise timing unavailable; measure on the app's own domain." });
    } catch { setPerf({ load_ms: null, source: "blocked", note: "Timing blocked by cross-origin policy." }); }
  };
  // Lightweight a11y scan (item 14): deterministic checks on the frame DOM where same-origin permits.
  const scanA11y = () => {
    try {
      const doc = frameRef.current?.contentDocument;
      if (!doc) { setA11y({ ok: null, note: "Cross-origin preview — run the scan on the app's own domain.", issues: [] }); return; }
      const issues = [];
      const imgs = [...doc.querySelectorAll("img")].filter(i => !i.getAttribute("alt")).length;
      if (imgs) issues.push(`${imgs} image(s) missing alt text`);
      const inputs = [...doc.querySelectorAll("input,select,textarea")].filter(i => !i.getAttribute("aria-label") && !doc.querySelector(`label[for="${i.id}"]`)).length;
      if (inputs) issues.push(`${inputs} form control(s) without a label`);
      if (!doc.querySelector("h1")) issues.push("No top-level heading (h1)");
      if (!doc.documentElement.getAttribute("lang")) issues.push("Document has no lang attribute");
      setA11y({ ok: issues.length === 0, issues, note: "Basic deterministic checks — not a full audit." });
    } catch { setA11y({ ok: null, note: "Scan blocked by cross-origin policy.", issues: [] }); }
  };

  // Opt-in preview recording (item 15): OFF by default, explicit informed consent each time.
  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    const ok = await confirm({ title: "Record this preview?", body: "This captures a short screen recording of the preview for your own reference or sharing. Recording is off by default and starts only with your explicit consent now. It stops when you press stop.", confirmLabel: "I consent — start recording" });
    if (!ok) return;
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const rec = new MediaRecorder(stream); const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => { stream.getTracks().forEach(t => t.stop()); const blob = new Blob(chunks, { type: "video/webm" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `preview-${projectId}.webm`; a.click(); setRecording(false); };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch { toast.error("Screen-capture permission is needed to record."); }
  };

  const dv = DEVICES[device];
  const liveMeta = live === "updating" ? { c: "k-tag-info", t: "Refreshing to match build" } : { c: "k-muted", t: "Settled — showing current state" };

  return (
    <div className="k-container" data-testid="preview-panel" style={{ padding: 14, display: "flex", flexDirection: "column" }}>
      {/* header: environment label (item 7, always visible) + live indicator + actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>Live preview</h3>
          <span className="k-badge" data-testid="preview-env-label" title="What a visitor on this link would see">{STATE_LABEL[state] || state}</span>
          {jumpAt && <span className="k-muted" data-testid="preview-jump-marker" style={{ fontSize: 12 }}>· as of {String(jumpAt.at).slice(11, 19)}</span>}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button className="k-btn k-btn-ghost" data-testid="preview-open" onClick={onOpen} style={{ padding: "5px 8px" }} aria-label="Open preview in new tab"><ExternalLink size={13} /></button>
          <button className="k-btn k-btn-ghost" data-testid="preview-refresh" onClick={() => refresh(false)} style={{ padding: "5px 8px" }} aria-label="Refresh preview"><RefreshCw size={13} /></button>
          <button className="k-btn k-btn-ghost" data-testid="preview-hard-refresh" onClick={() => refresh(true)} style={{ padding: "5px 8px" }} aria-label="Hard refresh (bypass cache)" title="Hard refresh — bypass cached assets"><RotateCw size={13} /></button>
          <button className="k-btn k-btn-primary" data-testid="preview-publish" onClick={doPublish} style={{ padding: "5px 10px" }}><Rocket size={13} /> Publish</button>
        </div>
      </div>

      {/* toolbar: device toggle, zoom, auto-refresh, compare, share, record, scans */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <div style={{ display: "flex", border: "1px solid var(--control)", borderRadius: 8, overflow: "hidden" }} role="group" aria-label="Device viewport">
          {Object.entries(DEVICES).map(([k, d]) => {
            const I = d.icon;
            return <button key={k} data-testid={`device-${k}`} onClick={() => setDevice(k)} aria-label={d.label} title={d.label}
              className={`k-btn ${device === k ? "k-btn-primary" : "k-btn-ghost"}`} style={{ padding: "4px 8px" }}><I size={13} /></button>;
          })}
        </div>
        <button className="k-btn k-btn-ghost" data-testid="zoom-out" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(2)))} style={{ padding: "4px 7px" }} aria-label="Zoom out"><ZoomOut size={13} /></button>
        <span className="k-muted" data-testid="zoom-level" style={{ fontSize: 12, minWidth: 38, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
        <button className="k-btn k-btn-ghost" data-testid="zoom-in" onClick={() => setZoom(z => Math.min(2, +(z + 0.1).toFixed(2)))} style={{ padding: "4px 7px" }} aria-label="Zoom in"><ZoomIn size={13} /></button>
        <label className="k-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} data-testid="auto-refresh-toggle">
          <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} aria-label="Auto-refresh preview" /> <Radio size={12} /> Auto-refresh
        </label>
        <button className={`k-btn ${compare ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="compare-toggle" onClick={() => setCompare(c => !c)} style={{ padding: "4px 8px", fontSize: 12 }}><Columns2 size={13} /> Compare</button>
        <button className="k-btn k-btn-ghost" data-testid="preview-share" onClick={share} style={{ padding: "4px 8px", fontSize: 12 }}><Share2 size={13} /> Share</button>
        <button className={`k-btn ${recording ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="preview-record" onClick={toggleRecord} style={{ padding: "4px 8px", fontSize: 12 }}><Video size={13} /> {recording ? "Stop" : "Record"}</button>
        <button className="k-btn k-btn-ghost" data-testid="perf-scan" onClick={measurePerf} style={{ padding: "4px 8px", fontSize: 12 }}><Gauge size={13} /> Performance</button>
        <button className="k-btn k-btn-ghost" data-testid="a11y-scan" onClick={scanA11y} style={{ padding: "4px 8px", fontSize: 12 }}><Accessibility size={13} /> A11y scan</button>
      </div>

      <div className={liveMeta.c} data-testid="preview-live-indicator" style={{ fontSize: 12, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
        <History size={12} /> {liveMeta.t}
      </div>

      {/* preview surface (with optional before/after compare) */}
      <div style={{ position: "relative", flex: 1, minHeight: 320, display: "flex", gap: 8, overflow: "auto", justifyContent: "center", background: "var(--canvas)", border: "1px solid var(--control)", borderRadius: 8 }}>
        {runtimeErr && (
          <div data-testid="preview-error-overlay" role="alert" style={{ position: "absolute", inset: 0, background: "rgba(20,20,22,.92)", color: "#fca5a5", padding: 16, zIndex: 5, fontSize: 13, overflow: "auto" }}>
            <strong>Runtime error in preview</strong>
            <p className="mono" style={{ marginTop: 6 }}>{runtimeErr}</p>
            <button className="k-btn k-btn-ghost" onClick={() => setRuntimeErr(null)} style={{ marginTop: 8 }}>Dismiss</button>
          </div>
        )}
        {src ? (
          <>
            <div style={{ width: dv.w === "100%" ? "100%" : dv.w, transform: `scale(${zoom})`, transformOrigin: "top center", transition: "width .2s" }}>
              {compare && <div className="k-muted" style={{ fontSize: 11, textAlign: "center" }}>Current</div>}
              <iframe title="preview" ref={frameRef} data-testid="preview-frame" src={src} style={{ width: "100%", height: 480, border: 0, borderRadius: 8, background: "#fff" }} />
            </div>
            {compare && (
              <div style={{ width: dv.w === "100%" ? "100%" : dv.w, transform: `scale(${zoom})`, transformOrigin: "top center" }} data-testid="compare-previous">
                <div className="k-muted" style={{ fontSize: 11, textAlign: "center" }}>Last published{dep?.last_published_subdomain ? "" : " (not yet published)"}</div>
                <iframe title="compare" data-testid="compare-frame" src={dep?.last_published_subdomain ? `https://${dep.last_published_subdomain}` : "about:blank"} style={{ width: "100%", height: 480, border: 0, borderRadius: 8, background: "#fff" }} />
              </div>
            )}
          </>
        ) : (
          <p className="k-muted" style={{ fontSize: 13, textAlign: "center", padding: 20, alignSelf: "center" }}>
            Your app preview appears here as it's built. State: <strong>{state}</strong> (Published / Paused / Private).
          </p>
        )}
      </div>

      {/* history scrubber (item 3) — steps back through recent preview states */}
      {dep?.history?.length > 0 && (
        <div data-testid="preview-scrubber" style={{ marginTop: 8 }}>
          <input type="range" min={0} max={dep.history.length - 1} defaultValue={dep.history.length - 1}
            aria-label="Preview history" style={{ width: "100%" }}
            onChange={(e) => { const h = dep.history[Number(e.target.value)]; toast.info(`Preview state: ${h.state} · ${String(h.at).slice(0, 19).replace("T", " ")}`); }} />
          <p className="k-muted" style={{ fontSize: 11 }}>Step back through {dep.history.length} recent preview states.</p>
        </div>
      )}

      {/* signals */}
      {perf && <p className="k-muted" data-testid="perf-result" style={{ fontSize: 12, marginTop: 8 }}>Load {perf.load_ms != null ? `${perf.load_ms}ms` : "—"}{perf.dom_ms ? ` · DOM ${perf.dom_ms}ms` : ""}. {perf.note || "Real navigation timing — a genuine measurement, not a score."}</p>}
      {a11y && <div data-testid="a11y-result" className={a11y.ok ? "k-tag-green" : "k-tag-warn"} style={{ fontSize: 12, marginTop: 6 }}>{a11y.ok === null ? a11y.note : a11y.ok ? "No obvious accessibility issues found (basic scan)." : `Accessibility: ${a11y.issues.join("; ")}. ${a11y.note}`}</div>}

      {/* shareable links list */}
      {shares.length > 0 && (
        <div data-testid="preview-shares" style={{ marginTop: 8 }}>
          <p className="k-muted" style={{ fontSize: 12, marginBottom: 4 }}>Shareable preview links (not published):</p>
          {shares.map(s => (
            <div key={s.token} data-testid={`share-${s.token}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0" }}>
              <span className={s.revoked ? "k-muted" : "k-tag-info"} style={{ textDecoration: s.revoked ? "line-through" : "none" }}>{s.revoked ? "revoked" : "active"}</span>
              <span className="mono" style={{ flex: 1, wordBreak: "break-all" }}>…/preview-share/{s.token.slice(0, 10)}…</span>
              {!s.revoked && <button className="k-btn k-btn-ghost" data-testid={`share-revoke-${s.token}`} onClick={() => revoke(s.token)} style={{ padding: "2px 8px" }}>Revoke</button>}
            </div>
          ))}
        </div>
      )}

      {/* pre-publish checklist (item 12) */}
      {showCheck && checklist && (
        <div className="k-container" data-testid="pre-publish-checklist" style={{ padding: 12, marginTop: 8, border: "1px solid var(--accent)" }}>
          <strong style={{ fontSize: 13 }}>Before you publish</strong>
          {checklist.items.map(it => (
            <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 4 }}>
              <span className={it.ok ? "k-tag-green" : "k-tag-warn"}>{it.ok ? "✓" : "!"}</span> {it.label}
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="k-btn k-btn-primary" data-testid="confirm-publish" onClick={confirmPublish}>{checklist.can_publish ? "Publish now" : "Publish anyway"}</button>
            <button className="k-btn k-btn-ghost" onClick={() => setShowCheck(false)}>Cancel</button>
          </div>
          {!checklist.can_publish && <p className="k-muted" style={{ fontSize: 11, marginTop: 6 }}>{checklist.blocking_count} item(s) worth resolving first — this is a real check, not a blind click.</p>}
        </div>
      )}
    </div>
  );
}
